// src/components/settings/JobTemplatesSettings.js
// ─────────────────────────────────────────────────────────────────────────────
// Job Templates — reusable job configurations for Weekly Planner + job form
// Hour-unit line items track individual task duration and auto-calc invoice qty
// Total labour validated against template duration with clear mismatch warning
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase/config';
import {
    collection, query, orderBy, onSnapshot,
    addDoc, setDoc, deleteDoc, doc, serverTimestamp
} from 'firebase/firestore';
import { can, PERMISSIONS } from '../../utils/permissions';
import { getArrivalWindows, getArrivalWindowLabel, resolveArrivalWindowKey } from '../../utils/arrivalWindows'; // aw-jts-92a3b4c5
import {
    Plus, X, Edit, Trash2, FileText, Clock,
    Package, Wrench, AlertTriangle, Info, Globe
} from 'lucide-react';
import ConfirmationModal from '../common/ConfirmationModal';
import { pickerItems } from '../../utils/catalogUi'; // catscreen-7a2c4e91 — pickers hide archived items
import LineDescriptionInput from '../documents/LineDescriptionInput'; // linetext-4d8b2e61 — Enter, indents and "- " bullets in line descriptions

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS & HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const UNITS = [
    { value: 'Hour',  label: 'per Hour',  isLabour: true  },
    { value: 'Unit',  label: 'per Unit',  isLabour: false },
    { value: 'SqFt',  label: 'per Sq Ft', isLabour: false },
    { value: 'Ft',    label: 'per Ft',    isLabour: false },
];

const blankLineItem = () => ({
    serviceId:    '',
    name:         '',
    description:  '',
    quantity:     1,
    durationMins: 60,
    price:        0,
    unit:         'Unit',
    type:         'Service',
    taxCodeId:    '',
    taxExempt:    false, // tmplexempt-5c9d1f38 -- the Tax Exempt tick, carried onto jobs, estimates and invoices made from the template
    crewGroupId:  '',   // which route group handles this line item
});

const blankTemplate = () => ({
    name:              '',
    defaultNotes:      '',
    estimatedDuration: 60,
    durationType:      'hours',   // 'hours' | 'days'
    durationDays:      1,
    defaultWindow:     'allday',
    lineItems:         [blankLineItem()],
    isBookable:        false,
    showBookingPrice:  false,
});

const formatMins = (m) => {
    if (!m && m !== 0) return '—';
    const h = Math.floor(m / 60), min = m % 60;
    if (h === 0) return `${min}m`;
    return min === 0 ? `${h}h` : `${h}h ${min}m`;
};

// ─────────────────────────────────────────────────────────────────────────────
// LINE ITEM ROW — all fields always visible
// ─────────────────────────────────────────────────────────────────────────────
const LineItemRow = ({ item, index, services, onChange, onRemove, canRemove, orgId, routeGroups }) => {
    const isHour = item.unit === 'Hour';
    const set = (field, value) => onChange(index, { ...item, [field]: value });

    const [showAddForm, setShowAddForm] = useState(false);
    const [newSvc, setNewSvc] = useState({ name: '', price: '', unit: 'Unit', type: 'Service' });
    const [isSavingNewSvc, setIsSavingNewSvc] = useState(false);
    const [addSvcError, setAddSvcError] = useState('');

    const handleServicePick = (serviceId) => {
        if (!serviceId) {
            onChange(index, { ...item, serviceId: '', name: '', price: 0, unit: 'Unit', type: 'Service', taxCodeId: '' });
            return;
        }
        const svc = services.find(s => s.id === serviceId);
        if (svc) {
            onChange(index, {
                ...item,
                serviceId:   svc.id,
                name:        svc.name,
                description: svc.description || '',
                price:       svc.price || 0,
                unit:        svc.unit || 'Unit',
                type:        svc.type || 'Service',
                taxCodeId:   svc.taxCodeId || '',
            });
        }
    };

    const handleSaveNewSvc = async () => {
        setAddSvcError('');
        if (!newSvc.name.trim()) { setAddSvcError('Name is required.'); return; }
        if (!newSvc.price || Number(newSvc.price) <= 0) { setAddSvcError('Enter a valid price.'); return; }
        if (!orgId) return;
        setIsSavingNewSvc(true);
        try {
            const docRef = await addDoc(collection(db, `organizations/${orgId}/services`), {
                name: newSvc.name.trim(),
                price: Number(newSvc.price),
                unit: newSvc.unit,
                type: newSvc.type,
                description: '',
                taxCodeId: null,
                estimatedDuration: null,
                isBookable: false,
            });
            // Auto-select the new service on this line item
            onChange(index, {
                ...item,
                serviceId:   docRef.id,
                name:        newSvc.name.trim(),
                description: '',
                price:       Number(newSvc.price),
                unit:        newSvc.unit,
                type:        newSvc.type,
                taxCodeId:   '',
            });
            setNewSvc({ name: '', price: '', unit: 'Unit', type: 'Service' });
            setShowAddForm(false);
        } catch (err) {
            console.error('Failed to save new service:', err);
            setAddSvcError('Failed to save. Please try again.');
        }
        setIsSavingNewSvc(false);
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {/* Header row */}
            <div className="flex items-center justify-between px-4 pt-3 pb-1">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Line Item {index + 1}
                </span>
                {canRemove && (
                    <button type="button" onClick={() => onRemove(index)}
                        className="text-gray-300 hover:text-red-500 transition-colors rounded p-0.5">
                        <X size={15} />
                    </button>
                )}
            </div>

            <div className="px-4 pb-4 space-y-3">
                {/* Name */}
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                        Name <span className="text-gray-400 font-normal">— pick from catalog, or type a custom name</span>
                    </label>
                        <div className="relative">
                        <select
                            value={item.serviceId || ''}
                            onChange={e => handleServicePick(e.target.value)}
                            className="w-full appearance-none p-2.5 pr-8 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
                        >
                            <option value="">— Choose from catalog —</option>
                            {pickerItems(services, item.serviceId).map(s => ( /* catscreen-7a2c4e91 — archived items not offered */
                                <option key={s.id} value={s.id}>{s.pickerLabel} · ${s.price}/{s.unit || 'Unit'}</option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                        </div>
                        {!item.serviceId && !showAddForm && (
                            <button
                                type="button"
                                onClick={() => setShowAddForm(true)}
                                className="mt-1.5 flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium py-1 px-1.5 rounded hover:bg-blue-50 transition-colors"
                            >
                                <Plus size={13} /> Not in your catalog? Add it now
                            </button>
                        )}
                        {!item.serviceId && showAddForm && (
                            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
                                <p className="text-xs font-semibold text-blue-800">Add to Services catalog — it'll be available everywhere</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="col-span-2">
                                        <input
                                            value={newSvc.name}
                                            onChange={e => setNewSvc(p => ({ ...p, name: e.target.value }))}
                                            placeholder="Service name, e.g. Lawn Mowing"
                                            className="w-full p-2 border border-blue-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                    <div className="relative">
                                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">$</span>
                                        <input
                                            type="number" min="0" step="0.01"
                                            value={newSvc.price}
                                            onChange={e => setNewSvc(p => ({ ...p, price: e.target.value }))}
                                            placeholder="Price"
                                            className="w-full pl-6 p-2 border border-blue-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                    <select
                                        value={newSvc.unit}
                                        onChange={e => setNewSvc(p => ({ ...p, unit: e.target.value }))}
                                        className="w-full p-2 border border-blue-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="Unit">per Unit</option>
                                        <option value="Hour">per Hour</option>
                                        <option value="SqFt">per SqFt</option>
                                        <option value="Ft">per Ft</option>
                                    </select>
                                    <select
                                        value={newSvc.type}
                                        onChange={e => setNewSvc(p => ({ ...p, type: e.target.value }))}
                                        className="w-full p-2 border border-blue-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="Service">Service</option>
                                        <option value="Material">Material</option>
                                    </select>
                                    <div className="col-span-2 flex gap-2">
                                        <button
                                            type="button"
                                            onClick={handleSaveNewSvc}
                                            disabled={isSavingNewSvc}
                                            className="flex-1 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                        >
                                            {isSavingNewSvc ? 'Saving…' : 'Save to catalog & use here'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setShowAddForm(false); setNewSvc({ name: '', price: '', unit: 'Unit', type: 'Service' }); setAddSvcError(''); }}
                                            className="px-3 py-1.5 bg-white border border-blue-300 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                    {addSvcError && <p className="col-span-2 text-xs text-red-600">{addSvcError}</p>}
                                </div>
                            </div>
                        )}
                </div>

                {/* Unit · Type · Price — always visible */}
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Unit</label>
                        <div className="relative">
                            <select
                                value={item.unit}
                                onChange={e => set('unit', e.target.value)}
                                className="w-full appearance-none p-2.5 pr-8 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
                            >
                                {UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
                        <div className="relative">
                            <select
                                value={item.type}
                                onChange={e => set('type', e.target.value)}
                                className="w-full appearance-none p-2.5 pr-8 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
                            >
                                <option value="Service">Service</option>
                                <option value="Material">Material</option>
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Price</label>
                        <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium select-none pointer-events-none">$</span>
                            <input
                                type="number" min="0" step="0.01"
                                value={item.price === 0 ? '' : item.price}
                                onChange={e => set('price', e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                                onFocus={e => e.target.select()}
                                placeholder="0.00"
                                className="w-full pl-7 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>
                    </div>
                </div>

                {/* Hour: task duration. Others: quantity */}
                {isHour ? (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                        <label className="block text-xs font-semibold text-blue-800 mb-1.5 flex items-center gap-1.5">
                            <Clock size={13} /> Time for this task (minutes)
                        </label>
                        <div className="flex items-center gap-3 flex-wrap">
                            <input
                                type="number" min="1" step="5"
                                value={item.durationMins || 60}
                                onChange={e => set('durationMins', parseInt(e.target.value) || 0)}
                                className="w-24 p-2 border border-blue-300 rounded-lg text-sm text-center bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                            <div className="text-xs text-blue-700">
                                = <strong>{((item.durationMins || 60) / 60).toFixed(2).replace(/\.?0+$/, '')} hrs</strong> billed
                                {item.price > 0 && (
                                    <span className="ml-2 font-semibold text-blue-800">
                                        · ${((item.durationMins || 60) / 60 * item.price).toFixed(2)}
                                    </span>
                                )}
                            </div>
                        </div>
                        <p className="text-[11px] text-blue-600 mt-1.5">
                            Invoice quantity auto-fills from this time when a job is created.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-3 items-end">
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Quantity</label>
                            <input
                                type="number" min="0" step="0.5"
                                value={item.quantity}
                                onChange={e => set('quantity', parseFloat(e.target.value) || 0)}
                                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>
                        {item.price > 0 && item.quantity > 0 && (
                            <p className="text-sm text-gray-500 pb-2.5">
                                = <span className="font-semibold text-gray-800">${(item.quantity * item.price).toFixed(2)}</span>
                            </p>
                        )}
                    </div>
                )}

                {/* Description — the shared field (its own label, placeholder and look) */}
                <LineDescriptionInput
                    value={item.description || ''}
                    onChange={e => set('description', e.target.value)}
                    labelClassName="block text-xs font-semibold text-gray-600 mb-1"
                />

                {/* tmplexempt-5c9d1f38 -- Tax Exempt, the same small tick the job form has on each line. */}
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id={`tmpl-taxExempt-${index}`}
                        checked={item.taxExempt === true}
                        onChange={e => set('taxExempt', e.target.checked)}
                        className="h-3 w-3 text-blue-600 border-gray-300 rounded"
                    />
                    <label htmlFor={`tmpl-taxExempt-${index}`} className="ml-1 text-xs text-gray-500 select-none">Tax Exempt</label>
                </div>

                {/* Crew assignment — optional, for multi-crew templates */}
                {routeGroups?.length > 0 && (
                    <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">
                            Crew <span className="font-normal">(optional — assign to a specific crew for multi-crew jobs)</span>
                        </label>
                        <div className="relative">
                            <select
                                value={item.crewGroupId || ''}
                                onChange={e => set('crewGroupId', e.target.value)}
                                className={`w-full appearance-none p-2 pr-8 border rounded-lg text-xs bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer transition-colors ${
                                    item.crewGroupId ? 'border-blue-300 bg-blue-50 text-blue-800' : 'border-gray-200 text-gray-500'
                                }`}
                            >
                                <option value="">No crew assigned</option>
                                {routeGroups.map(g => (
                                    <option key={g.id} value={g.id}>{g.name}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
                                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                        {item.crewGroupId && (
                            <p className="text-[11px] text-blue-600 mt-1.5">
                                Jobs from this template will assign this line item to <strong>{routeGroups.find(g => g.id === item.crewGroupId)?.name || 'this crew'}</strong>.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// DURATION MISMATCH BANNER
// ─────────────────────────────────────────────────────────────────────────────
const DurationMismatchBanner = ({ lineItems, manualDuration }) => {
    // Only relevant when there are NO Hour items and the manual duration is set.
    // When Hour items exist, the duration IS their sum — there's nothing to mismatch against.
    const hourItems = lineItems.filter(i => i.unit === 'Hour');
    if (hourItems.length > 0) return null; // auto-calculated — no mismatch possible

    // Non-Hour items: check if the user has set a manual duration but no labour items
    // to account for it. Informational only — not a blocking error.
    const hasNonHourItems = lineItems.some(i => i.unit !== 'Hour' && (i.name || '').trim());
    if (!hasNonHourItems || !manualDuration) return null;

    // If only materials/units and a manual duration — gently remind them to add labour items
    return (
        <div className="flex items-start gap-2.5 p-3 rounded-xl border bg-sky-50 border-sky-200 text-sky-800 text-sm mt-3">
            <Info size={15} className="flex-shrink-0 mt-0.5" />
            <p className="text-xs">
                You have a <strong>{formatMins(manualDuration)}</strong> job duration set but no labour (per Hour) line items.
                Consider adding a labour line item so the invoice reflects the time billed.
            </p>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE CARD (list)
// ─────────────────────────────────────────────────────────────────────────────
const TemplateCard = ({ template, onEdit, onDelete }) => {
    const totalValue = (template.lineItems || []).reduce((s, i) => {
        const qty = i.unit === 'Hour'
            ? (i.durationMins || template.estimatedDuration || 60) / 60
            : (i.quantity || 1);
        return s + qty * (i.price || 0);
    }, 0);

    return (
        <div className="group bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 hover:border-blue-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <FileText size={20} className="text-blue-600" />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 text-base leading-tight">{template.name}</h3>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Clock size={11} />
                                {template.durationType === 'days'
                                    ? `${template.durationDays || 1} day${(template.durationDays || 1) !== 1 ? 's' : ''}`
                                    : formatMins(template.estimatedDuration)}
                            </span>
                            <span className="text-xs text-gray-400">·</span>
                            <span className="text-xs text-gray-500">
                                {getArrivalWindowLabel(template.defaultWindow)}
                            </span>
                            {totalValue > 0 && (
                                <>
                                    <span className="text-xs text-gray-400">·</span>
                                    <span className="text-xs font-semibold text-green-700">~${totalValue.toFixed(2)}</span>
                                </>
                            )}
                            {template.isBookable && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-bold flex items-center gap-1">
                                    <Globe size={9} /> Online{template.showBookingPrice ? ' · Shows Price' : ''}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button onClick={() => onEdit(template)}
                        className="p-2 rounded-lg bg-gray-50 hover:bg-blue-50 hover:text-blue-600 text-gray-500 transition-colors">
                        <Edit size={15} />
                    </button>
                    <button onClick={() => onDelete(template.id)}
                        className="p-2 rounded-lg bg-gray-50 hover:bg-red-50 hover:text-red-600 text-gray-500 transition-colors">
                        <Trash2 size={15} />
                    </button>
                </div>
            </div>

            <div className="space-y-1.5 mb-3">
                {(template.lineItems || []).map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                        <div className={`p-1 rounded ${item.type === 'Material' ? 'bg-orange-50 text-orange-500' : 'bg-blue-50 text-blue-500'}`}>
                            {item.type === 'Material' ? <Package size={10} /> : <Wrench size={10} />}
                        </div>
                        <span className="flex-1 truncate font-medium">{item.name || '—'}</span>
                        {item.unit === 'Hour' ? (
                            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-semibold">
                                {formatMins(item.durationMins || 60)}
                            </span>
                        ) : (
                            <span className="text-gray-400">{item.quantity} {item.unit}</span>
                        )}
                        {item.price > 0 && <span className="text-gray-500">${item.price}/{item.unit}</span>}
                    </div>
                ))}
            </div>

            {/* Multi-crew badge */}
            {(() => {
                const crewIds = [...new Set((template.lineItems || []).map(i => i.crewGroupId).filter(Boolean))];
                return crewIds.length > 1 ? (
                    <div className="flex items-center gap-1.5 mt-2">
                        <span className="text-[10px] font-bold px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full uppercase tracking-wide">Multi-crew</span>
                        <span className="text-xs text-gray-400">{crewIds.length} crews configured</span>
                    </div>
                ) : null;
            })()}

            {template.defaultNotes && (
                <p className="text-xs text-gray-400 italic border-t border-gray-100 pt-2 line-clamp-2">
                    "{template.defaultNotes}"
                </p>
            )}

            <div className="flex gap-2 mt-3 sm:hidden pt-3 border-t border-gray-100">
                <button onClick={() => onEdit(template)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-gray-50 text-gray-600 rounded-lg text-sm font-medium hover:bg-blue-50 hover:text-blue-600">
                    <Edit size={14} /> Edit
                </button>
                <button onClick={() => onDelete(template.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-gray-50 text-gray-600 rounded-lg text-sm font-medium hover:bg-red-50 hover:text-red-600">
                    <Trash2 size={14} /> Delete
                </button>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────
const JobTemplatesSettings = ({ currentUserProfile }) => {
    const [templates, setTemplates]   = useState([]);
    const [services, setServices]     = useState([]);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingId, setEditingId]   = useState(null);
    const [form, setForm]             = useState(blankTemplate());
    const [isSaving, setIsSaving]     = useState(false);
    const [error, setError]           = useState('');
    const [deleteId, setDeleteId]     = useState(null);

    const orgId    = currentUserProfile?.organizationId;
    const canWrite = can(currentUserProfile, PERMISSIONS.MANAGE_JOBS);

    useEffect(() => {
        if (!orgId) return;
        return onSnapshot(
            query(collection(db, `organizations/${orgId}/jobTemplates`), orderBy('name')),
            snap => setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
    }, [orgId]);

    useEffect(() => {
        if (!orgId) return;
        return onSnapshot(
            query(collection(db, `organizations/${orgId}/services`), orderBy('name')),
            snap => setServices(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
    }, [orgId]);

    const [routeGroups, setRouteGroups] = useState([]);
    useEffect(() => {
        if (!orgId) return;
        return onSnapshot(
            query(collection(db, `organizations/${orgId}/routeGroups`), orderBy('name')),
            snap => setRouteGroups(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
    }, [orgId]);

    const totalLabourMins = form.lineItems
        .filter(i => i.unit === 'Hour')
        .reduce((s, i) => s + (i.durationMins || 60), 0);

    const openCreate = () => {
        setEditingId(null); setForm(blankTemplate()); setError(''); setIsFormOpen(true);
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    };

    const openEdit = (t) => {
        setEditingId(t.id);
        setForm({
            name:              t.name || '',
            defaultNotes:      t.defaultNotes || '',
            estimatedDuration: t.estimatedDuration || 60,
            durationType:      t.durationType || 'hours',
            durationDays:      t.durationDays || 1,
            defaultWindow:     resolveArrivalWindowKey(t.defaultWindow, 'allday'),
            isBookable:        t.isBookable || false,
            showBookingPrice:  t.showBookingPrice || false,
            lineItems: (t.lineItems?.length > 0 ? t.lineItems : [blankLineItem()]).map(i => ({
                ...blankLineItem(), ...i,
                durationMins: i.durationMins ?? (i.unit === 'Hour' ? (t.estimatedDuration || 60) : 60),
            })),
        });
        setError(''); setIsFormOpen(true);
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    };

    const closeForm = () => { setIsFormOpen(false); setEditingId(null); setForm(blankTemplate()); setError(''); };

    const handleLineItemChange = useCallback((idx, updated) => {
        setForm(prev => { const items = [...prev.lineItems]; items[idx] = updated; return { ...prev, lineItems: items }; });
    }, []);

    const addLineItem = () => setForm(prev => ({ ...prev, lineItems: [...prev.lineItems, blankLineItem()] }));
    const removeLineItem = (idx) => setForm(prev => ({ ...prev, lineItems: prev.lineItems.filter((_, i) => i !== idx) }));

    const handleSave = async () => {
        if (!orgId || !canWrite) return;
        setError('');
        if (!form.name.trim()) return setError('Template name is required.');
        const validItems = form.lineItems.filter(i => (i.name || '').trim() || i.serviceId);
        if (validItems.length === 0) return setError('Add at least one line item with a name.');

        setIsSaving(true);
        try {
            const payload = {
                name:              form.name.trim(),
                defaultNotes:      form.defaultNotes.trim(),
                estimatedDuration: form.durationType === 'days'
                    ? form.durationDays * 24 * 60   // store as mins for backward compat
                    : totalLabourMins > 0 ? totalLabourMins : (Number(form.estimatedDuration) || 60),
                durationType:      form.durationType || 'hours',
                durationDays:      form.durationType === 'days' ? (Number(form.durationDays) || 1) : null,
                defaultWindow:     form.durationType === 'days' ? 'anytime' : form.defaultWindow,
                isBookable:        form.isBookable || false,
                showBookingPrice:  form.isBookable ? (form.showBookingPrice || false) : false,
                lineItems: validItems.map(i => ({
                    serviceId:    i.serviceId    || '',
                    name:         (i.name || '').trim(),
                    description:  i.description  || '',
                    quantity:     i.unit === 'Hour' ? (i.durationMins || 60) / 60 : (Number(i.quantity) || 1),
                    durationMins: i.unit === 'Hour' ? (i.durationMins || 60) : null,
                    price:        Number(i.price)  || 0,
                    unit:         i.unit          || 'Unit',
                    type:         i.type          || 'Service',
                    taxCodeId:    i.taxCodeId     || '',
                    taxExempt:    i.taxExempt === true, // tmplexempt-5c9d1f38
                    ...(typeof i.salesTaxCodeId === 'string' && i.salesTaxCodeId ? { salesTaxCodeId: i.salesTaxCodeId } : {}), // taxlines-771ce983 — the code only; a line made from the template takes its rates as they are then
                    crewGroupId:  i.crewGroupId   || '',
                    crewGroupName: i.crewGroupId
                        ? (routeGroups.find(g => g.id === i.crewGroupId)?.name || '')
                        : '',
                })),
                updatedAt: serverTimestamp(),
            };

            if (editingId) {
                await setDoc(doc(db, `organizations/${orgId}/jobTemplates`, editingId), payload, { merge: true });
            } else {
                await addDoc(collection(db, `organizations/${orgId}/jobTemplates`), {
                    ...payload, createdAt: serverTimestamp(), createdBy: currentUserProfile.id,
                });
            }
            closeForm();
        } catch (err) {
            console.error('Save template failed:', err);
            setError('Failed to save. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!orgId || !deleteId) return;
        await deleteDoc(doc(db, `organizations/${orgId}/jobTemplates`, deleteId));
        setDeleteId(null);
    };

    if (!canWrite) return (
        <div className="p-6 text-center">
            <AlertTriangle size={28} className="text-amber-400 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Admin or Manager access required to manage job templates.</p>
        </div>
    );

    return (
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Job Templates</h2>
                    <p className="text-sm text-gray-500 mt-1 max-w-xl">
                        Reusable job configurations. Used by the <strong>Weekly Planner</strong> and the <strong>New Job</strong> form to pre-fill titles, line items, and notes instantly. Assign crews to line items to create <strong>multi-crew templates</strong>.
                    </p>
                </div>
                {!isFormOpen && (
                    <button onClick={openCreate}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm flex-shrink-0">
                        <Plus size={16} /> New Template
                    </button>
                )}
            </div>

            {templates.length === 0 && !isFormOpen && (
                <div className="mt-4 mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <p className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2"><Info size={14} /> How templates work</p>
                    <ul className="space-y-2 text-xs text-blue-700">
                        <li className="flex items-start gap-2">
                            <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center font-bold text-[10px] mt-0.5">1</span>
                            <span><strong>Labour items (per Hour)</strong> — set the time per task (e.g. 60 min mowing, 30 min weeding). Invoice quantity auto-calculates from actual scheduled time.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center font-bold text-[10px] mt-0.5">2</span>
                            <span><strong>Material/supply items (per Unit etc.)</strong> — set a fixed quantity. Never changes based on duration.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center font-bold text-[10px] mt-0.5">3</span>
                            <span><strong>Default notes</strong> — describe what's included in this type of job. Customer-specific notes (e.g. "don't cut near pool") belong on the customer record.</span>
                        </li>
                    </ul>
                </div>
            )}

            {/* ── FORM ── */}
            {isFormOpen && (
                <div className="mt-4 mb-8 p-4 sm:p-6 bg-gray-50 rounded-2xl border border-gray-200">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="font-bold text-gray-800 text-lg">{editingId ? 'Edit Template' : 'New Job Template'}</h3>
                        <button onClick={closeForm} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"><X size={20} /></button>
                    </div>

                    <div className="space-y-6">
                        {/* Name */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Template Name <span className="font-normal text-gray-400">(becomes the job title)</span>
                            </label>
                            <input
                                value={form.name}
                                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                placeholder="e.g. Lawn Mowing – Weekly, Ceramic Coat Full Detail, HVAC Tune-Up…"
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                            />
                        </div>

                        {/* Duration display + window */}
                        <div className="space-y-4">
                            {/* Duration mode toggle */}
                            <div className="p-4 bg-white border border-gray-200 rounded-xl space-y-3">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Job Duration</p>

                                {/* Hours vs Days toggle */}
                                <div className="flex rounded-lg overflow-hidden border border-gray-300 w-fit">
                                    <button type="button"
                                        onClick={() => setForm(p => ({ ...p, durationType: 'hours' }))}
                                        className={`px-4 py-2 text-sm font-medium transition-colors ${form.durationType !== 'days' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        Hours / Minutes
                                    </button>
                                    <button type="button"
                                        onClick={() => setForm(p => ({ ...p, durationType: 'days' }))}
                                        className={`px-4 py-2 text-sm font-medium transition-colors ${form.durationType === 'days' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        Days
                                    </button>
                                </div>

                                {form.durationType === 'days' ? (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="number" min="1" max="365" step="1"
                                                value={form.durationDays}
                                                onChange={e => setForm(p => ({ ...p, durationDays: parseInt(e.target.value) || 1 }))}
                                                className="w-24 p-2.5 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                            <span className="text-sm text-gray-500">
                                                {form.durationDays === 1 ? 'day' : 'days'} — all-day calendar block
                                            </span>
                                        </div>
                                        {/* Quick picks */}
                                        <div className="flex gap-1.5 flex-wrap">
                                            {[[1,'1d'],[3,'3d'],[5,'5d'],[7,'1wk'],[14,'2wk'],[30,'1mo']].map(([d,label]) => (
                                                <button key={label} type="button"
                                                    onClick={() => setForm(p => ({ ...p, durationDays: d }))}
                                                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${form.durationDays === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                        <p className="text-xs text-gray-400">Creates a multi-day all-day event on the calendar. Arrival window is not used.</p>
                                    </div>
                                ) : (
                                    <>
                                        {totalLabourMins > 0 ? (
                                            <>
                                                <p className="text-3xl font-bold text-blue-700">{formatMins(totalLabourMins)}</p>
                                                <p className="text-xs text-gray-400">Calculated from your labour line items below</p>
                                            </>
                                        ) : (
                                            <>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="number" min="5" step="5"
                                                        value={form.estimatedDuration}
                                                        onChange={e => setForm(p => ({ ...p, estimatedDuration: parseInt(e.target.value) || 60 }))}
                                                        className="w-28 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                    />
                                                    <span className="text-sm text-gray-500">minutes</span>
                                                </div>
                                                <p className="text-xs text-gray-400">Add labour (per Hour) items below to set time per task instead</p>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Arrival window — hidden for day-duration templates */}
                            {form.durationType !== 'days' && (
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Default Arrival Window</label>
                                    <div className="relative">
                                        <select
                                            value={form.defaultWindow}
                                            onChange={e => setForm(p => ({ ...p, defaultWindow: e.target.value }))}
                                            className="w-full appearance-none p-3 pr-10 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm cursor-pointer"
                                        >
                                            {getArrivalWindows().map(w => <option key={w.key} value={w.key}>{w.label}</option>)}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Line items */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <label className="block text-sm font-semibold text-gray-700">Line Items</label>
                                <span className="text-[11px] text-blue-600 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full flex items-center gap-1">
                                    <Clock size={11} /> Per Hour items track time per task
                                </span>
                            </div>
                            <div className="space-y-3">
                                {form.lineItems.map((item, idx) => (
                                    <LineItemRow
                                        key={idx}
                                        item={item}
                                        index={idx}
                                        services={services}
                                        onChange={handleLineItemChange}
                                        onRemove={removeLineItem}
                                        canRemove={form.lineItems.length > 1}
                                        orgId={orgId}
                                    routeGroups={routeGroups}
                                    />
                                ))}
                            </div>
                            <button type="button" onClick={addLineItem}
                                className="mt-3 flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium py-1 px-2 rounded-lg hover:bg-blue-50 transition-colors">
                                <Plus size={15} /> Add line item
                            </button>
                            <DurationMismatchBanner lineItems={form.lineItems} manualDuration={totalLabourMins === 0 ? form.estimatedDuration : 0} />
                        </div>

                        {/* Default notes */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                Default Job Notes <span className="font-normal text-gray-400">(optional)</span>
                            </label>
                            <p className="text-xs text-gray-400 mb-2">
                                Describe what's included in <strong>this type of job</strong>. These notes appear on every job created from this template.
                                For notes specific to an individual customer (e.g. "don't cut near the pool"), add them on the <strong>customer record</strong> instead.
                            </p>
                            <textarea rows={3}
                                value={form.defaultNotes}
                                onChange={e => setForm(p => ({ ...p, defaultNotes: e.target.value }))}
                                placeholder="e.g. Includes mowing, edging and pathway debris clearing. Gate code required."
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm resize-none"
                            />
                        </div>

                        {/* Online Booking Toggles */}
                        <div className="rounded-xl border border-gray-200 overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3.5 bg-white">
                                <div>
                                    <p className="text-sm font-semibold text-gray-800">Show on booking page</p>
                                    <p className="text-xs text-gray-500 mt-0.5">Customers can select this as a service when booking online</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setForm(p => ({ ...p, isBookable: !p.isBookable, showBookingPrice: !p.isBookable ? p.showBookingPrice : false }))}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${form.isBookable ? 'bg-blue-600' : 'bg-gray-200'}`}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.isBookable ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>
                            {form.isBookable && (
                                <div className="flex items-center justify-between px-4 py-3.5 bg-gray-50 border-t border-gray-200">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-800">Show price on booking page</p>
                                        <p className="text-xs text-gray-500 mt-0.5">Display the estimated job total to customers</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setForm(p => ({ ...p, showBookingPrice: !p.showBookingPrice }))}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${form.showBookingPrice ? 'bg-blue-600' : 'bg-gray-200'}`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.showBookingPrice ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                                <AlertTriangle size={14} /> {error}
                            </div>
                        )}

                        <div className="flex gap-3 pt-1">
                            <button onClick={handleSave} disabled={isSaving}
                                className="flex-1 sm:flex-none sm:px-8 py-3 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm flex items-center justify-center gap-2">
                                {isSaving ? 'Saving…' : (editingId ? 'Update Template' : 'Save Template')}
                            </button>
                            <button onClick={closeForm}
                                className="flex-1 sm:flex-none sm:px-6 py-3 border border-gray-300 text-gray-600 rounded-xl font-medium text-sm hover:bg-gray-50 transition-colors">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* LIST */}
            {templates.length === 0 && !isFormOpen ? (
                <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                    <FileText size={36} className="text-gray-300 mx-auto mb-3" />
                    <h3 className="text-sm font-semibold text-gray-500 mb-1">No templates yet</h3>
                    <p className="text-xs text-gray-400 mb-4 max-w-xs mx-auto">
                        Templates let you pre-fill job titles, line items, and notes — perfect for mowing rounds, detail packages, and any recurring service.
                    </p>
                    <button onClick={openCreate}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors">
                        <Plus size={15} /> Create your first template
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {templates.map(t => (
                        <TemplateCard key={t.id} template={t} onEdit={openEdit} onDelete={setDeleteId} />
                    ))}
                </div>
            )}

            <ConfirmationModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Delete Template"
                message="Are you sure? Jobs already created from this template are unaffected."
                confirmText="Yes, Delete"
            />
        </div>
    );
};

export default JobTemplatesSettings;
export { LineItemRow }; // linetext-4d8b2e61 — for src/utils/lineDescriptionEnter.test.js (Enter in a template line's description)